import { CLASSES_RUBRICA, salvarPrevistoSchema, type ClasseRubrica, type PrevistoLinhaResponse, type SalvarPrevistoDto } from '@triade/contracts';
import { decimalDigitado, decimalParaTextoCampo } from '@/lib/format';
import { deslocarMes } from './carteira-mes';

export { deslocarMes, mesAtual, rotuloMes } from './carteira-mes';

/** So texto: rotulos das classes de rubrica (pt-BR). Nenhum valor monetario aqui. */
export const ROTULO_CLASSE: Record<ClasseRubrica, string> = {
  CUSTO_FIXO: 'Custo fixo',
  DESPESA_FIXA: 'Despesa fixa',
  FINANCEIRO: 'Financeiro',
  DEDUCAO_RECEITA: 'Dedução da receita',
  CAIXA_NAO_RESULTADO: 'Caixa (não é resultado)',
};

export const CLASSES_ORDEM: readonly ClasseRubrica[] = CLASSES_RUBRICA;

const RE_MES = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Busca da URL do custo de fabrica: `mes` (AAAA-MM). Valor invalido e descartado (volta ao mes atual). */
export function lerBuscaCustoFabrica(bruto: Record<string, unknown>): { mes?: string } {
  const mes = typeof bruto.mes === 'string' && RE_MES.test(bruto.mes) ? bruto.mes : undefined;
  return mes ? { mes } : {};
}

/** Meses anteriores ao da tela, do mais recente ao mais antigo (origem da copia do previsto). */
export function mesesDeOrigem(mes: string, quantos = 12): string[] {
  return Array.from({ length: quantos }, (_, i) => deslocarMes(mes, -(i + 1)));
}

/** Texto digitado por rubrica: valor (pt-BR, sem conta) e data de pagamento (ISO ou vazio). */
export interface LinhaRascunho {
  valor: string;
  dataPagamento: string;
}

/** Valores da API no texto dos campos ("1500.5" vira "1500,5"; null vira vazio = ainda nao lancado). */
export function rascunhoDe(linhas: readonly PrevistoLinhaResponse[]): Record<string, LinhaRascunho> {
  return Object.fromEntries(
    linhas.map((l) => [l.rubricaId, { valor: decimalParaTextoCampo(l.valor), dataPagamento: l.dataPagamento ?? '' }]),
  );
}

export type MontagemPrevisto = { ok: true; dto: SalvarPrevistoDto } | { ok: false; mensagem: string };

/** Texto do rascunho para o corpo da API. Campo vazio = null (sem lancamento); a validacao e o schema compartilhado. */
export function montarPrevisto(linhas: readonly PrevistoLinhaResponse[], rascunho: Record<string, LinhaRascunho>): MontagemPrevisto {
  const dto: SalvarPrevistoDto = {
    linhas: linhas.map((l) => {
      const r = rascunho[l.rubricaId] ?? { valor: '', dataPagamento: '' };
      const texto = r.valor.trim();
      return {
        rubricaId: l.rubricaId,
        valor: texto === '' ? null : decimalDigitado(texto),
        dataPagamento: r.dataPagamento.trim() === '' ? null : r.dataPagamento.trim(),
      };
    }),
  };
  const linhaInvalida = dto.linhas.findIndex((l, i) => l.valor === null && (rascunho[linhas[i]!.rubricaId]?.valor.trim() ?? '') !== '');
  if (linhaInvalida >= 0) {
    return { ok: false, mensagem: `Valor inválido em "${linhas[linhaInvalida]!.nome}": use número com até 2 casas (ex.: 1.500,25)` };
  }
  const valido = salvarPrevistoSchema.safeParse(dto);
  if (!valido.success) {
    const issue = valido.error.issues[0];
    const linha = typeof issue?.path[1] === 'number' ? linhas[issue.path[1]] : undefined;
    return { ok: false, mensagem: linha ? `${issue?.message ?? 'Dados inválidos'} (${linha.nome})` : (issue?.message ?? 'Dados do previsto inválidos') };
  }
  return { ok: true, dto };
}
